import React, { useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { useAuth } from '../../Constant/Auth';
import * as Constants from '../../Constant/constant';
import '../parentTables.css';
import SearchableSelect from '../../shared/SearchableSelect';

import {
  getBooksByLibrary,
  getReservationsByLibrary,
  getMyMemberProfile,
  addReservation,
  getBookById,
  getAvailableCopiesByBookId,
  searchBooksByLibrary,
  getErrorMessage
} from '../../api/api';

const AVAILABILITY_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'not_available', label: 'Not Available' }
];

const ParentBookList = () => {
  const { user } = useAuth();

  const libraryId = user?.library_id || user?.libraryId;

  // user.id is the login/users table id, NOT the members table id — the
  // member profile itself (with its real id) is resolved server-side via
  // getMyMemberProfile() below, from the verified login, never guessed
  // client-side from user.id.
  const explicitMemberId =
    user?.member_id ||
    user?.memberId ||
    user?.parent_member_id ||
    user?.parentMemberId ||
    user?.library_member_id ||
    user?.libraryMemberId ||
    '';

  const explicitMembershipId =
    user?.membership_id ||
    user?.membershipId ||
    user?.active_membership_id ||
    user?.activeMembershipId ||
    user?.library_membership_id ||
    user?.libraryMembershipId ||
    '';

  const [books, setBooks] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [currentMember, setCurrentMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const [requestLoadingId, setRequestLoadingId] = useState(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = show full list
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = useRef(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterAvailability, setFilterAvailability] = useState('');

  // Book detail modal
  const [detailBook, setDetailBook] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailCopies, setDetailCopies] = useState([]);

  const safeArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.rows)) return data.rows;
    return [];
  };

  const getMemberIdFromMember = (member) => {
    return member?.id || member?.member_id || member?.Member_ID || '';
  };

  const getMembershipIdFromMember = (member) => {
    return (
      member?.membership_id ||
      member?.active_membership_id ||
      member?.membershipId ||
      member?.activeMembershipId ||
      member?.library_membership_id ||
      member?.libraryMembershipId ||
      ''
    );
  };

  const getEffectiveMemberId = () => {
    return (
      explicitMemberId ||
      getMemberIdFromMember(currentMember) ||
      ''
    );
  };

  const getEffectiveMembershipId = () => {
    return (
      explicitMembershipId ||
      getMembershipIdFromMember(currentMember) ||
      ''
    );
  };

  const fetchData = async () => {
    try {
      if (!libraryId) {
        console.warn('[PARENT_BOOK_LIST][NO_LIBRARY_ID]', { user });
        return;
      }

      setLoading(true);

      // Self-scoped: the backend resolves "my" member profile from the
      // verified login (email + library), never from a client-supplied id
      // or a whole-library dump matched by guesswork — so this can never
      // resolve to another member's record.
      const [bookData, reservationData, myProfile] = await Promise.all([
        getBooksByLibrary(libraryId),
        getReservationsByLibrary(libraryId),
        getMyMemberProfile().catch(() => null)
      ]);

      const bookRows = safeArray(bookData);
      const reservationRows = safeArray(reservationData);

      console.log('[PARENT_BOOK_LIST][FETCH_RESPONSE]', {
        books: bookRows,
        reservations: reservationRows,
        myProfile
      });

      setBooks(bookRows);
      setCurrentMember(myProfile);

      const effectiveMemberId =
        explicitMemberId ||
        getMemberIdFromMember(myProfile);

      const effectiveMembershipId =
        explicitMembershipId ||
        getMembershipIdFromMember(myProfile);

      const myReservations = reservationRows.filter((item) => {
        return (
          (effectiveMemberId &&
            String(item.member_id || item.user_id || item.parent_id || '') ===
              String(effectiveMemberId)) ||
          (effectiveMembershipId &&
            String(item.membership_id || '') === String(effectiveMembershipId))
        );
      });

      setReservations(myReservations);
    } catch (error) {
      console.error('[PARENT_BOOK_LIST][FETCH_ERROR]', {
        error,
        response: error?.response?.data,
        message: error?.message
      });

      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId, explicitMemberId, explicitMembershipId]);

  const getImageUrl = (path) => {
    if (!path) return null;
    if (String(path).startsWith('http')) return path;

    const cleanPath = String(path).replace(/\\/g, '/').replace(/^\/+/, '');
    return `${Constants.IMAGE_BASE_URL}/${cleanPath}`;
  };

  const getExistingRequest = (bookId) => {
    return reservations.find((item) => {
      const status = String(item.status || '').toLowerCase();

      const activeStatus = [
        'requested',
        'approved',
        'ready_for_pickup'
      ].includes(status);

      return String(item.book_id) === String(bookId) && activeStatus;
    });
  };

  const getAvailableCopies = (book) => {
    if (book.available_copies !== undefined && book.available_copies !== null) {
      return Number(book.available_copies);
    }

    if (book.availableCopies !== undefined && book.availableCopies !== null) {
      return Number(book.availableCopies);
    }

    if (book.available !== undefined && book.available !== null) {
      return Number(book.available);
    }

    return 0;
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!search.trim()) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await searchBooksByLibrary(libraryId, search.trim());
        setSearchResults(Array.isArray(data) ? data : []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 400);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, libraryId]);

  const handleViewDetail = async (bookId) => {
    setDetailBook(null);
    setDetailCopies([]);
    setDetailLoading(true);
    try {
      const [bookData, copiesData] = await Promise.all([
        getBookById(bookId),
        getAvailableCopiesByBookId(bookId).catch(() => [])
      ]);
      setDetailBook(bookData || null);
      setDetailCopies(Array.isArray(copiesData) ? copiesData : []);
    } catch {
      /* silently fall back */
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRequestBook = async (book) => {
    const memberId = getEffectiveMemberId();
    const membershipId = getEffectiveMembershipId();

    console.log('[PARENT_BOOK_LIST][REQUEST_CLICK]', {
      book,
      user,
      currentMember,
      libraryId,
      memberId,
      membershipId
    });

    if (!libraryId) {
      Swal.fire('Error', 'Library ID not found', 'error');
      return;
    }

    if (!memberId) {
      Swal.fire('Error', 'Parent/member ID not found', 'error');
      return;
    }

    if (!membershipId) {
      console.error('[PARENT_BOOK_LIST][NO_MEMBERSHIP_ID]', {
        user,
        currentMember
      });

      Swal.fire(
        'Error',
        'Your membership record could not be found. Please contact the library.',
        'error'
      );
      return;
    }

    if (!book?.id) {
      Swal.fire('Error', 'Book ID not found', 'error');
      return;
    }

    const existingRequest = getExistingRequest(book.id);

    if (existingRequest) {
      Swal.fire(
        'Already Requested',
        'You already have an active request for this book.',
        'warning'
      );
      return;
    }

    const availableCopies = getAvailableCopies(book);

    if (availableCopies <= 0) {
      Swal.fire(
        'Not Available',
        'No copy is currently available for this book.',
        'warning'
      );
      return;
    }

    const confirm = await Swal.fire({
      title: 'Request Book?',
      text: `Do you want to request "${book.title || 'this book'}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, request',
      cancelButtonText: 'Cancel'
    });

    if (!confirm.isConfirmed) return;

    const payload = {
      library_id: libraryId,
      membership_id: membershipId,
      member_id: memberId,
      student_id:
        user?.student_id ||
        user?.linked_student_id ||
        currentMember?.student_id ||
        currentMember?.linked_student_id ||
        null,
      book_id: book.id,
      reserved_copy_id: null,
      requested_at: new Date().toISOString().slice(0, 10),
      status: 'requested',
      remarks: 'Requested by parent'
    };

    try {
      setRequestLoadingId(book.id);

      console.log('[PARENT_BOOK_LIST][ADD_RESERVATION_PAYLOAD]', payload);

      const response = await addReservation(payload);

      console.log('[PARENT_BOOK_LIST][ADD_RESERVATION_RESPONSE]', response);

      Swal.fire('Success', 'Book request sent successfully.', 'success');

      await fetchData();
    } catch (error) {
      console.error('[PARENT_BOOK_LIST][REQUEST_ERROR]', {
        error,
        response: error?.response?.data,
        message: error?.message,
        payload
      });

      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setRequestLoadingId(null);
    }
  };

  const categories = [...new Set(books.map((b) => b.category_name).filter(Boolean))].sort();

  const baseBooks = search.trim() && searchResults !== null ? searchResults : books;
  const displayBooks = baseBooks.filter((book) => {
    if (filterCategory && book.category_name !== filterCategory) return false;
    const avail = getAvailableCopies(book);
    if (filterAvailability === 'available' && avail <= 0) return false;
    if (filterAvailability === 'not_available' && avail > 0) return false;
    return true;
  });

  return (
    <div className="container-fluid mt-3 pt-page">
      <div className="pt-page-header">
        <div className="pt-page-header__left">
          <span className="pt-page-header__icon">
            <i className="fas fa-book" />
          </span>
          <div>
            <h3 className="pt-page-header__title">Book List</h3>
            <div className="pt-page-header__subtitle">Search books and send request to librarian</div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mt-3 table-full-card">
        <div className="card-body">
          <div className="pt-filter-row" style={{ marginBottom: 16 }}>
            {/* Search */}
            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
              <div className="input-group" style={{ height: 42 }}>
                <span className="input-group-text" style={{ background: '#f8fafc', borderColor: '#dbe4f0' }}>
                  {searchLoading
                    ? <span className="spinner-border spinner-border-sm text-primary" />
                    : <i className="fas fa-search text-muted" />}
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by book, author, publisher, ISBN…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ height: 42 }}
                />
                {search && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => { setSearch(''); setSearchResults(null); }}
                    title="Clear"
                    style={{ height: 42 }}
                  >
                    <i className="fas fa-times" />
                  </button>
                )}
              </div>
              {search.trim() && !searchLoading && searchResults !== null && (
                <div className="form-text mt-1">
                  {displayBooks.length} result{displayBooks.length !== 1 ? 's' : ''} for &ldquo;{search}&rdquo;
                </div>
              )}
            </div>

            {/* Category filter */}
            <div style={{ flex: '0 0 200px' }}>
              <SearchableSelect
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                options={categories.map((cat) => ({ value: cat, label: cat }))}
                placeholder="All Categories"
              />
            </div>

            {/* Availability filter */}
            <div style={{ flex: '0 0 160px' }}>
              <SearchableSelect
                value={filterAvailability}
                onChange={(e) => setFilterAvailability(e.target.value)}
                options={AVAILABILITY_OPTIONS}
                placeholder="All"
              />
            </div>

            {/* Reset */}
            <div style={{ flex: '0 0 auto' }}>
              <button
                type="button"
                className="btn btn-outline-secondary"
                style={{ height: 42, whiteSpace: 'nowrap', width: 'auto' }}
                onClick={() => {
                  setSearch('');
                  setSearchResults(null);
                  setFilterCategory('');
                  setFilterAvailability('');
                }}
              >
                <i className="fas fa-undo me-1" />Reset
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4 table-full-card">
              Loading...
            </div>
          ) : (
            <div className="table-responsive table-full-card">
              <table className="table table-bordered table-hover align-middle text-center">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Image</th>
                    <th>Book</th>
                    <th>Category</th>
                    <th>Author</th>
                    <th>Publisher</th>
                    <th>ISBN</th>
                    <th>Available</th>
                    <th>Details</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {displayBooks.length > 0 ? (
                    displayBooks.map((book, index) => {
                      const existingRequest = getExistingRequest(book.id);
                      const availableCopies = getAvailableCopies(book);
                      const isRequestLoading =
                        String(requestLoadingId) === String(book.id);

                      return (
                        <tr key={book.id}>
                          <td>{index + 1}</td>

                          <td>
                            {book.cover_image ? (
                              <img
                                src={getImageUrl(book.cover_image)}
                                alt={book.title}
                                style={{
                                  width: 70,
                                  height: 80,
                                  objectFit: 'cover',
                                  borderRadius: 6
                                }}
                              />
                            ) : (
                              <span className="text-muted">No Image</span>
                            )}
                          </td>

                          <td>
                            <strong>{book.title || '-'}</strong>

                            {book.subtitle && (
                              <div className="small text-muted">
                                {book.subtitle}
                              </div>
                            )}
                          </td>

                          <td>{book.category_name || '-'}</td>
                          <td>{book.author_name || '-'}</td>
                          <td>{book.publisher_name || '-'}</td>
                          <td>{book.isbn || '-'}</td>

                          <td>
                            <span
                              className={`badge ${
                                availableCopies > 0
                                  ? 'bg-success'
                                  : 'bg-danger'
                              }`}
                            >
                              {availableCopies}
                            </span>
                          </td>

                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-info"
                              onClick={() => handleViewDetail(book.id)}
                              title="View book details"
                            >
                              Details
                              {/* <i className="fas fa-info-circle me-1" /> */}
                            </button>
                          </td>

                          <td>
                            {(() => {
                              const disabled =
                                Boolean(existingRequest) ||
                                availableCopies <= 0 ||
                                isRequestLoading;

                              let cls = 'btn btn-sm btn-success';
                              let label = (
                                <><i className="fas fa-bookmark me-1" />Request Book</>
                              );

                              if (isRequestLoading) {
                                cls = 'btn btn-sm btn-primary';
                                label = (
                                  <><i className="fas fa-spinner fa-spin me-1" />Requesting…</>
                                );
                              } else if (existingRequest) {
                                cls = 'btn btn-sm btn-secondary';
                                label = (
                                  <><i className="fas fa-check me-1" />Request Sent</>
                                );
                              } else if (availableCopies <= 0) {
                                cls = 'btn btn-sm btn-danger';
                                label = (
                                  <><i className="fas fa-ban me-1" />Not Available</>
                                );
                              }

                              return (
                                <button
                                  type="button"
                                  className={cls}
                                  style={{ minWidth: 132, whiteSpace: 'nowrap' }}
                                  disabled={disabled}
                                  onClick={() => handleRequestBook(book)}
                                >
                                  {label}
                                </button>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center text-muted py-4">
                        No books found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Book Detail Modal */}
      {(detailLoading || detailBook) && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1060,
            background: 'rgba(0,0,0,0.48)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
          }}
          onClick={() => { setDetailBook(null); setDetailCopies([]); }}
        >
          <div
            style={{
              background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560,
              boxShadow: '0 24px 60px rgba(0,0,0,.22)', overflow: 'hidden',
              maxHeight: '90vh', display: 'flex', flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 20px', flexShrink: 0,
              background: 'linear-gradient(135deg,#1e3a8a,#2563eb)', color: '#fff'
            }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>
                <i className="fas fa-book-open me-2" />Book Details
              </span>
              <button
                type="button"
                onClick={() => { setDetailBook(null); setDetailCopies([]); }}
                style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
              >×</button>
            </div>

            {/* Body */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {detailLoading ? (
                <div style={{ textAlign: 'center', padding: '48px 0', color: '#64748b' }}>
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: 28 }} />
                  <div style={{ marginTop: 10, fontSize: 13 }}>Loading book details…</div>
                </div>
              ) : detailBook ? (() => {
                const b = detailBook;
                const availableCopies = getAvailableCopies(b);
                const coverUrl = getImageUrl(b.cover_image || b.image || b.book_image);

                const fields = [
                  ['Author',        b.author_name || b.author || '-'],
                  ['Publisher',     b.publisher_name || b.publisher || '-'],
                  ['Category',      b.category_name || b.category || '-'],
                  ['ISBN',          b.isbn || '-'],
                  ['Language',      b.language || '-'],
                  ['Edition',       b.edition || '-'],
                  ['Year',          b.publication_year || b.year || '-'],
                  ['Pages',         b.pages || b.no_of_pages || '-'],
                  ['Total Copies',  b.total_copies ?? '-'],
                  ['Available',     availableCopies],
                ].filter(([, v]) => v !== '-' && v !== undefined && v !== null && v !== '');

                return (
                  <div style={{ padding: '20px 24px' }}>
                    {/* Cover + title */}
                    <div style={{ display: 'flex', gap: 18, marginBottom: 20 }}>
                      <div style={{ flexShrink: 0 }}>
                        {coverUrl ? (
                          <img
                            src={coverUrl}
                            alt={b.title}
                            style={{ width: 90, height: 110, objectFit: 'cover', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,.15)' }}
                          />
                        ) : (
                          <div style={{
                            width: 90, height: 110, borderRadius: 8, background: '#e2e8f0',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8'
                          }}>
                            <i className="fas fa-book" style={{ fontSize: 28 }} />
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h5 style={{ margin: 0, fontWeight: 800, color: '#1e293b', lineHeight: 1.3 }}>
                          {b.title || '-'}
                        </h5>
                        {b.subtitle && (
                          <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>{b.subtitle}</div>
                        )}
                        <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{
                            background: availableCopies > 0 ? '#dcfce7' : '#fee2e2',
                            color: availableCopies > 0 ? '#166534' : '#991b1b',
                            padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700
                          }}>
                            {availableCopies > 0 ? `${availableCopies} Available` : 'Not Available'}
                          </span>
                          {b.status && (
                            <span style={{
                              background: '#eff6ff', color: '#1d4ed8',
                              padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600
                            }}>
                              {String(b.status).charAt(0).toUpperCase() + String(b.status).slice(1)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Fields grid */}
                    {fields.length > 0 && (
                      <dl style={{
                        display: 'grid', gridTemplateColumns: '130px 1fr',
                        rowGap: 8, columnGap: 12,
                        background: '#f8fafc', borderRadius: 10,
                        padding: '14px 16px', margin: '0 0 16px'
                      }}>
                        {fields.map(([label, value]) => (
                          <React.Fragment key={label}>
                            <dt style={{ fontSize: 12, color: '#64748b', fontWeight: 600, margin: 0 }}>{label}</dt>
                            <dd style={{ fontSize: 13, color: '#1e293b', margin: 0, wordBreak: 'break-word' }}>{value}</dd>
                          </React.Fragment>
                        ))}
                      </dl>
                    )}

                    {/* Available copies */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: .5 }}>
                        <i className="fas fa-copy me-1" />Available Copies
                        <span style={{
                          marginLeft: 8, background: detailCopies.length > 0 ? '#dcfce7' : '#fee2e2',
                          color: detailCopies.length > 0 ? '#166534' : '#991b1b',
                          padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700
                        }}>
                          {detailCopies.length}
                        </span>
                      </div>

                      {detailCopies.length === 0 ? (
                        <div style={{
                          padding: '12px 14px', borderRadius: 8, background: '#fef2f2',
                          border: '1px solid #fecaca', fontSize: 13, color: '#991b1b',
                          display: 'flex', alignItems: 'center', gap: 8
                        }}>
                          <i className="fas fa-times-circle" />
                          No copies currently available for borrowing
                        </div>
                      ) : (
                        <div style={{
                          border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden'
                        }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' }}>
                                {['Copy #', 'Accession No', 'Barcode', 'Condition'].map((h) => (
                                  <th key={h} style={{ padding: '7px 10px', color: '#fff', fontWeight: 600, textAlign: 'left', border: 'none' }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {detailCopies.map((copy, i) => {
                                const condition = String(copy.condition || copy.book_return_condition || copy.condition_status || 'good').toLowerCase();
                                const condColor = condition === 'good' ? { bg: '#f0fdf4', text: '#16a34a' }
                                  : condition === 'damaged' ? { bg: '#fef9c3', text: '#854d0e' }
                                  : { bg: '#f1f5f9', text: '#475569' };
                                return (
                                  <tr key={copy.id || i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '7px 10px', fontWeight: 600, color: '#1e293b' }}>
                                      {copy.copy_number || copy.copy_no || (i + 1)}
                                    </td>
                                    <td style={{ padding: '7px 10px', color: '#374151' }}>
                                      {copy.accession_no || '-'}
                                    </td>
                                    <td style={{ padding: '7px 10px', color: '#374151' }}>
                                      {copy.barcode || '-'}
                                    </td>
                                    <td style={{ padding: '7px 10px' }}>
                                      <span style={{
                                        background: condColor.bg, color: condColor.text,
                                        padding: '2px 8px', borderRadius: 10, fontWeight: 600, textTransform: 'capitalize'
                                      }}>
                                        {condition}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    {(b.description || b.about || b.summary) && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: .5 }}>
                          About this book
                        </div>
                        <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.65, margin: 0 }}>
                          {b.description || b.about || b.summary}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })() : null}
            </div>

            {/* Footer */}
            {detailBook && !detailLoading && (() => {
              const alreadyRequested = Boolean(getExistingRequest(detailBook.id));
              const notAvailable = getAvailableCopies(detailBook) <= 0;

              let reqCls = 'btn btn-success';
              let reqLabel = (<><i className="fas fa-bookmark me-1" />Request Book</>);
              if (alreadyRequested) {
                reqCls = 'btn btn-secondary';
                reqLabel = (<><i className="fas fa-check me-1" />Request Sent</>);
              } else if (notAvailable) {
                reqCls = 'btn btn-danger';
                reqLabel = (<><i className="fas fa-ban me-1" />Not Available</>);
              }

              return (
                <div style={{
                  padding: '14px 24px', borderTop: '1px solid #e8edf5',
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                  gap: 10, flexShrink: 0, background: '#fafbfc'
                }}>
                  <button
                    type="button"
                    className="btn btn-light"
                    style={{ minWidth: 120, whiteSpace: 'nowrap' }}
                    onClick={() => { setDetailBook(null); setDetailCopies([]); }}
                  >
                    <i className="fas fa-times me-1" />Close
                  </button>
                  <button
                    type="button"
                    className={reqCls}
                    style={{ minWidth: 150, whiteSpace: 'nowrap' }}
                    disabled={alreadyRequested || notAvailable}
                    onClick={() => { setDetailBook(null); handleRequestBook(detailBook); }}
                  >
                    {reqLabel}
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentBookList;
